import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {ErrorModel} from '../../../../app.model';
import {AppStore} from '../../../../store/store';
import {Segment} from '../../../segment.model';
import {segmentListForValidationSelector} from '../../../segment.reducer';
import {SegmentService} from '../../../segment.service';
import {SegmentValidationModel} from '../validation.model';
import {
  segmentValidationErrorSelector,
  segmentValidationIsCreatedSelector,
  segmentValidationIsUpdatedSelector
} from '../validation.reducer';
import {SegmentValidationService} from '../validation.service';
import {SubSink} from 'subsink';
import {Store} from '@ngrx/store';
import {selectSegmentDetails} from '../../../segments.state';

@Component({
  selector: 'app-segment-validation-form',
  template: `
    <form [formGroup]="segmentValidationForm"
          (submit)="submit()"
          id="segment-validation-form"
          data-cy="form-segment-validation">
      <button mat-icon-button
              id="button-dialog-close"
              class="mat-icon-button-small"
              data-cy="button-dialog-close"
              [mat-dialog-close]="true">
        <mat-icon>clear</mat-icon>
      </button>
      <h5 mat-dialog-title>{{createMode ? 'Create' : 'Edit'}} Validation</h5>
      <div mat-dialog-content class="mat-body-1">
        <p>Create a new validation triplet.</p>
        <p [hidden]="!errorMessage" class="error" id="error-feedback">{{errorMessage}}</p>
        <mat-form-field>
          <input #validationName
                 formControlName="name"
                 id="segment-validation-name-input"
                 data-cy="input-segment-validation-name"
                 class="form-input"
                 matInput
                 maxlength="500"
                 name="validation-name"
                 placeholder="Name">
          <mat-hint align="end">{{validationName.value.length}} / 500</mat-hint>
          <mat-error *ngIf="segmentValidationForm.get('name')?.hasError('required')">
            Validation name is not defined
          </mat-error>
        </mat-form-field>

        <div class="validation-input-container">
          <mat-form-field id="segment-validation-positive-example-form-field">
            <input formControlName="positiveExample"
                   [matAutocomplete]="positiveAuto"
                   id="segment-validation-positive-input"
                   data-cy="input-segment-validation-positive"
                   matInput
                   name="positive"
                   placeholder="Positive Examples">
            <mat-error *ngIf="positiveExampleControl.hasError('required')">
              Positive Examples is mandatory
            </mat-error>
            <mat-autocomplete #positiveAuto="matAutocomplete" [displayWith]="displayFn">
              <mat-option *ngFor="let positiveSegment of filteredPositives | async | orderBy: 'id' | slice : 0 : 10"
                          [value]="positiveSegment"
                          data-cy="option-segment-validation-positive">
                {{ positiveSegment.id }} - {{positiveSegment.name}}
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>


          <mat-form-field id="segment-validation-total-population-form-field">
            <input formControlName="totalPopulation"
                   [matAutocomplete]="totalsAuto"
                   id="segment-validation-total-population-input"
                   data-cy="input-segment-validation-total-population"
                   matInput
                   name="total"
                   placeholder="Total Population">
            <mat-error *ngIf="totalPopulationControl.hasError('required')">
              Total Population Segment is mandatory
            </mat-error>
            <mat-autocomplete #totalsAuto="matAutocomplete" [displayWith]="displayFn">
              <mat-option *ngFor="let totalSegment of filteredTotals | async | slice : 0 : 10"
                          [value]="totalSegment"
                          data-cy="option-segment-validation-total-population">
                {{ totalSegment.id }} - {{totalSegment.name}}
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>

        </div>

      </div>
      <div mat-dialog-actions>
        <button type="button" (click)="reset()" mat-raised-button>Reset</button>
        <button type="submit"
                color="primary"
                id="button-segment-validation"
                data-cy="button-segment-validation-submit"
                mat-raised-button>Create
        </button>
      </div>
    </form>
  `,
  styleUrls: ['form.component.scss'],
})
export class SegmentValidationFormComponent implements OnInit, OnDestroy {

  createMode = true;

  allSegments: Segment[] = [];

  selectedSegment!: Segment;
  segmentValidation?: SegmentValidationModel;
  errorMessage!: string;

  subsink = new SubSink();
  filteredPositives!: Observable<Segment[]>;
  filteredTotals!: Observable<Segment[]>;

  positiveExampleControl = new FormControl(null, Validators.required);
  totalPopulationControl = new FormControl(null, Validators.required);

  segmentValidationForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.max(500)]),
    positiveExample: this.positiveExampleControl,
    totalPopulation: this.totalPopulationControl
  });

  constructor(private appStore: AppStore,
              private store: Store,
              private matDialog: MatDialog,
              private dialogRef: MatDialogRef<SegmentValidationFormComponent>,
              private segmentValidationsService: SegmentValidationService,
              private segmentsActions: SegmentService) {
  }

  ngOnInit(): void {
    this.segmentValidationsService.initDialog();
    this.subsink.sink = this.store.select(selectSegmentDetails)
      .subscribe(details => {
        this.selectedSegment = details?.segment || <Segment>{};
        if (!this.segmentValidationForm.get('name')?.value && this.selectedSegment) {
          this.segmentValidationForm.get('name')?.setValue(this.selectedSegment.name);
        }
      });

    // TODO: Check the intention of this
    this.subsink.sink = this.appStore.subscribe((state: AppStore) => {
      this.allSegments = segmentListForValidationSelector(state);
      this.filteredPositives = this.getFilteredSegments(this.positiveExampleControl);
      this.filteredTotals = this.getFilteredSegments(this.totalPopulationControl);

      if (segmentValidationIsCreatedSelector(state) || segmentValidationIsUpdatedSelector(state)) {
        this.errorMessage = '';
        this.dialogRef.close();
      } else {
        const validationErrors: ErrorModel = segmentValidationErrorSelector(state);

        if (validationErrors) {
          this.errorMessage = validationErrors.message;
          console.error(this.errorMessage);
        }
      }
    });
    this.segmentsActions.getSegmentListForValidation();

    this.subsink.sink = this.matDialog.afterAllClosed.subscribe(this.clear);
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  clear() {
    this.segmentValidation = undefined;
  }

  reset() {
    this.errorMessage = '';
    this.segmentValidationForm.reset(this.segmentValidation);
  }

  getFilteredSegments(formControl: FormControl): Observable<any[]> {
    return formControl.valueChanges
      .pipe(
        startWith(''),
        map((segment) => {
          return segment && typeof segment === 'object' ? segment.name : segment;
        }),
        map(
          (val) => {
            return val ? this.filter(val) : this.allSegments.slice();
          }
        )
      );
  }

  displayFn(positive: any): string {
    return positive ? positive.name : '';
  }

  filter(val: string) {
    return this.allSegments.filter((option) => {
      return new RegExp(val, 'gi').test(option.name + ' ' + option.id);
    });
  }

  setValidation(segmentValidationModel: SegmentValidationModel): void {

    if (segmentValidationModel) {
      this.createMode = false;
      this.segmentValidation = segmentValidationModel;
      this.segmentValidationForm.addControl('id', new FormControl(segmentValidationModel.id));
      this.segmentValidationForm.setValue(this.segmentValidation);
    }
  }

  submit() {
    if (this.segmentValidationForm.valid) {
      const segmentValidationModel = new SegmentValidationModel(this.segmentValidationForm.value);
      if (this.createMode) {
        this.segmentValidationsService.createValidation(this.selectedSegment.id, segmentValidationModel);
      } else {
        this.segmentValidationsService.updateValidation(segmentValidationModel);
      }
    }
  }

  close(): void {
    this.dialogRef.close();
  }

}
